import { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Card, Tab, TabGroup, TabList, TabPanel, TabPanels, Text, Title,
} from '@tremor/react';
import { fetchInventoryLines, fetchInventorySkus, fetchInventoryStores } from '../api/inventory';
import { fetchPartFilters } from '../api/drilldown';
import { fetchOverview } from '../api/client';
import ErrorRetry from '../components/ErrorRetry';
import TableSkeleton from '../components/TableSkeleton';
import { currentUser } from '../api/auth';
import { useStoreFilter } from '../lib/storeFilter';
import { t } from '../lib/i18n';
import type {
  ABCClass, InventoryLineRow, InventorySkuRow, InventoryStoreRow, OverviewPayload, User,
} from '../api/types';
import Header from '../components/Header';
import InfoTooltip from '../components/InfoTooltip';
import InventorySkuTable from '../components/InventorySkuTable';
import InventoryLineTable from '../components/InventoryLineTable';
import InventoryStoreTable from '../components/InventoryStoreTable';
import LineDetailModal from '../components/LineDetailModal';
import InventoryTrendStrip from '../components/InventoryTrendStrip';

const fmtMxn = new Intl.NumberFormat('es-MX', {
  style: 'currency',
  currency: 'MXN',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const fmtPct = new Intl.NumberFormat('es-MX', {
  style: 'percent',
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

export default function Inventory() {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    currentUser()
      .then((u) => { if (alive) setUser(u); })
      .catch(() => { if (alive) setUser(null); });
    return () => { alive = false; };
  }, []);
  if (user === undefined) return null;
  return user ? <InventoryInner user={user} /> : <Navigate to="/login" replace />;
}

export function InventoryInner({ user, embedded = false }: { user: User; embedded?: boolean }) {
  const [skus, setSkus] = useState<InventorySkuRow[] | null>(null);
  const [lines, setLines] = useState<InventoryLineRow[] | null>(null);
  const [storeRows, setStoreRows] = useState<InventoryStoreRow[] | null>(null);
  const [overview, setOverview] = useState<OverviewPayload | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [abcFilter, setAbcFilter] = useState<'all' | ABCClass>('all');
  const [deadOnly, setDeadOnly] = useState(false);
  // Shared header store (lib/storeFilter); InventoryLayout renders the picker.
  const [storeFilter] = useStoreFilter(user);

  // v2.57 — line drill-down. Descriptions come from /api/part-filters (live
  // daemon); fetched separately + best-effort so a daemon outage never breaks
  // the cache-backed inventory page (desc just falls back to "—").
  const [lineDesc, setLineDesc] = useState<Record<string, string>>({});
  const [modalLine, setModalLine] = useState<string | null>(null);
  const [modalSid, setModalSid] = useState<number | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  useEffect(() => {
    let alive = true;
    fetchPartFilters()
      .then((f) => {
        if (!alive) return;
        const m: Record<string, string> = {};
        for (const ln of f.lines) m[ln.code] = ln.desc;
        setLineDesc(m);
      })
      .catch(() => { /* desc is optional enrichment */ });
    return () => { alive = false; };
  }, []);
  const openLine = (line: string, sid: number) => {
    setModalLine(line);
    setModalSid(sid);
    setModalOpen(true);
  };

  // Fire all three fetches + overview (for store names) in parallel on mount.
  const load = () => {
    let alive = true;
    setError(null);
    Promise.all([fetchInventorySkus(), fetchInventoryLines(), fetchInventoryStores(), fetchOverview(user.role)])
      .then(([s, l, st, o]) => {
        if (!alive) return;
        setSkus(s); setLines(l); setStoreRows(st); setOverview(o);
      })
      .catch((e: Error) => { if (alive) setError(e.message); });
    return () => { alive = false; };
  };
  useEffect(() => load(), [user.role]);

  // The Go proxy strips cost fields for staff; we detect that by checking
  // whether any SKU row has `inv_cost_mxn` present. False → hide cost columns.
  const showCost = useMemo(() => {
    if (!skus || skus.length === 0) return user.role !== 'staff';
    return skus.some((r) => r.inv_cost_mxn !== undefined);
  }, [skus, user.role]);

  // Apply chip + dead filter + store scoping to the SKU table.
  const visibleSkus = useMemo(() => {
    if (!skus) return [];
    return skus.filter((r) => {
      if (storeFilter !== null && r.sid !== storeFilter) return false;
      if (abcFilter !== 'all' && r.abc_class !== abcFilter) return false;
      if (deadOnly && r.kind === 'normal') return false;
      return true;
    });
  }, [skus, abcFilter, deadOnly, storeFilter]);

  const visibleLines = useMemo(() => {
    if (!lines) return [];
    if (storeFilter === null) return lines;
    return lines.filter((r) => r.sid === storeFilter);
  }, [lines, storeFilter]);

  const visibleStores = useMemo(() => {
    if (!storeRows) return [];
    if (storeFilter === null) return storeRows;
    return storeRows.filter((r) => r.sid === storeFilter);
  }, [storeRows, storeFilter]);

  // KPI strip rollup over what's visible.
  const kpis = useMemo(() => {
    let invCost = 0, margin = 0, dead = 0, low = 0, aClass = 0;
    for (const r of visibleSkus) {
      if (r.inv_cost_mxn !== undefined && r.inv_cost_mxn > 0) {
        invCost += r.inv_cost_mxn;
        margin += r.margin90_mxn ?? 0;
      }
      if (r.kind === 'dead') dead += 1;
      if (r.kind === 'low')  low  += 1;
      if (r.abc_class === 'A') aClass += 1;
    }
    return {
      gmroi: invCost > 0 ? margin / invCost : null,
      invCost,
      dead,
      low,
      aClass,
    };
  }, [visibleSkus]);

  const showStoresTab = (overview?.stores?.length ?? 0) > 1;
  const overviewStores = overview?.stores ?? [];

  const content = (
    <>
        <Title>{t.inventory.title}</Title>
        <Text className="text-tremor-content text-sm">{t.inventory.subtitle}</Text>

        {error && (
          <ErrorRetry
            message={error.includes('preparación') ? t.inventory.notReady : error}
            onRetry={load}
          />
        )}
        {!skus && !error && <TableSkeleton rows={8} cols={6} />}

        {/* KPI strip — only meaningful for owner/manager (cost-bearing). */}
        {showCost && (
          <Card className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Kpi
              label={
                <InfoTooltip text={t.inventory.gmroiHelp}>
                  {t.inventory.kpi.chainGmroi}
                </InfoTooltip>
              }
              value={kpis.gmroi == null ? '—' : fmtPct.format(kpis.gmroi)}
            />
            <Kpi label={t.inventory.kpi.invCost}    value={fmtMxn.format(kpis.invCost)} />
            <Kpi label={t.inventory.kpi.dead}       value={String(kpis.dead)} />
            <Kpi label={t.inventory.kpi.low}        value={String(kpis.low)} />
            <Kpi label={t.inventory.kpi.aClass}     value={String(kpis.aClass)} />
          </Card>
        )}

        {/* Filter row */}
        <Card className="flex flex-wrap gap-4 items-center">
          {showCost && (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-tremor-content">{t.inventory.filter.abc}:</span>
              {(['all', 'A', 'B', 'C', '—'] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setAbcFilter(v)}
                  className={
                    'px-2 py-1 rounded border text-xs ' +
                    (abcFilter === v
                      ? 'bg-tremor-brand text-tremor-brand-inverted border-tremor-brand'
                      : 'border-tremor-border text-tremor-content hover:text-tremor-content-strong')
                  }
                >
                  {v === 'all' ? t.inventory.filter.abcAll : v}
                </button>
              ))}
            </div>
          )}
          <label className="text-sm flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={deadOnly} onChange={(e) => setDeadOnly(e.target.checked)} />
            <span>{t.inventory.filter.dead}</span>
          </label>
          <Text className="ml-auto text-xs text-tremor-content">
            {visibleSkus.length} SKUs · {visibleLines.length} {t.common.lines}
          </Text>
        </Card>

        {/* Trend strip — header-style sparkline cards over the daily snapshot
            stream. Sits above the existing tables to give buyers/managers a
            quick "is inventory healthy" pulse before drilling into rows. */}
        <InventoryTrendStrip role={user.role} sid={storeFilter} />

        {/* Tabs — two variants to keep Tremor TabList/TabPanels children strictly typed. */}
        {skus && lines && storeRows && (
          showStoresTab ? (
            <TabGroup>
              <TabList>
                <Tab>{t.inventory.tabs.skus}</Tab>
                <Tab>{t.inventory.tabs.lines}</Tab>
                <Tab>{t.inventory.tabs.stores}</Tab>
              </TabList>
              <TabPanels>
                <TabPanel>
                  <InventorySkuTable rows={visibleSkus} showCost={showCost} />
                </TabPanel>
                <TabPanel>
                  <InventoryLineTable rows={visibleLines} showCost={showCost} lineDesc={lineDesc} onRowClick={openLine} />
                </TabPanel>
                <TabPanel>
                  <InventoryStoreTable rows={visibleStores} stores={overviewStores} showCost={showCost} />
                </TabPanel>
              </TabPanels>
            </TabGroup>
          ) : (
            <TabGroup>
              <TabList>
                <Tab>{t.inventory.tabs.skus}</Tab>
                <Tab>{t.inventory.tabs.lines}</Tab>
              </TabList>
              <TabPanels>
                <TabPanel>
                  <InventorySkuTable rows={visibleSkus} showCost={showCost} />
                </TabPanel>
                <TabPanel>
                  <InventoryLineTable rows={visibleLines} showCost={showCost} lineDesc={lineDesc} onRowClick={openLine} />
                </TabPanel>
              </TabPanels>
            </TabGroup>
          )
        )}
        <LineDetailModal
          open={modalOpen}
          line={modalLine}
          sid={modalSid}
          showCost={showCost}
          onClose={() => setModalOpen(false)}
        />
    </>
  );

  if (embedded) return <div className="space-y-4">{content}</div>;
  return (
    <div className="min-h-screen flex flex-col">
      <Header user={user} storeFilter />
      <main className="flex-1 px-6 py-6 space-y-4 max-w-screen-2xl mx-auto w-full">
        {content}
      </main>
    </div>
  );
}

function Kpi({ label, value }: { label: React.ReactNode; value: string }) {
  return (
    <div>
      <div className="text-tremor-content text-xs">{label}</div>
      <div className="text-tremor-content-strong text-lg font-semibold">{value}</div>
    </div>
  );
}
